import { useState, useMemo } from 'react';
import { Package, CheckCircle, AlertTriangle, AlertCircle, Truck, LayoutDashboard, DollarSign, Activity, MapPin, Bell, Calendar } from 'lucide-react';
import { useTheme } from './context/ThemeContext';
import { getData, regenerateData } from './data';
import Header from './components/Header';
import StatCard from './components/StatCard';
import ShipmentsTable from './components/ShipmentsTable';
import ShipmentModal from './components/ShipmentModal';
import AnalyticsPanel from './components/AnalyticsPanel';
import CostImpactCalculator from './components/CostImpactCalculator';
import PredictiveAnalytics from './components/PredictiveAnalytics';
import RouteRiskHeatmap from './components/RouteRiskHeatmap';
import AlertsPanel from './components/AlertsPanel';
import ShipmentTimeline from './components/ShipmentTimeline';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

function App() {
  const { theme, toggleTheme, colors } = useTheme();
  const [data, setData] = useState(getData);
  const [selectedShipment, setSelectedShipment] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');

  const stats = useMemo(() => {
    const { shipments, excursions } = data;
    return {
      total: shipments.length,
      delivered: shipments.filter(s => s.status === 'Delivered').length,
      inTransit: shipments.filter(s => s.status === 'In Transit').length,
      withExcursions: shipments.filter(s => s.hasExcursion).length,
      critical: excursions.filter(e => e.severity === 'Critical').length,
      complianceRate: ((shipments.length - shipments.filter(s => s.hasExcursion).length) / shipments.length * 100).toFixed(1),
    };
  }, [data]);

  const handleGenerateReport = () => {
    const doc = new jsPDF();
    doc.setFontSize(18);
    doc.setTextColor(0, 212, 170);
    doc.text('ChainGuard Report', 20, 20);
    doc.setFontSize(10);
    doc.setTextColor(100);
    doc.text(`Generated: ${new Date().toLocaleString()}`, 20, 28);
    
    autoTable(doc, {
      startY: 45,
      head: [['Severity', 'Product', 'Duration', 'Deviation', 'Cause']],
      body: data.excursions.slice(0, 20).map(e => [
        e.severity,
        data.shipments.find(s => s.id === e.shipmentId)?.productName?.split(' ')[0] || 'N/A',
        `${e.durationMinutes}m`,
        `+${e.maxDeviationC}°C`,
        e.rootCause
      ]),
      headStyles: { fillColor: [0, 212, 170] },
      styles: { fontSize: 8 }
    });
    
    doc.save('ChainGuard_Report.pdf');
  };

  const tabs = [
    { id: 'overview', icon: LayoutDashboard, label: 'Dashboard' },
    { id: 'cost', icon: DollarSign, label: 'Cost Impact' },
    { id: 'predictive', icon: Activity, label: 'Analytics' },
    { id: 'routes', icon: MapPin, label: 'Routes' },
    { id: 'timeline', icon: Calendar, label: 'Timeline' },
    { id: 'alerts', icon: Bell, label: 'Alerts' },
  ];

  return (
    <div style={{ 
      minHeight: '100vh', 
      background: colors.bgPrimary,
      
    }}>
      <Header 
        theme={theme}
        onThemeToggle={toggleTheme}
        onRefresh={() => setData(regenerateData())}
        lastUpdated={data.generatedAt}
        onGenerateReport={handleGenerateReport}
        shipments={data.shipments}
        excursions={data.excursions}
        onSelectShipment={setSelectedShipment}
        onNavigateToAlerts={() => setActiveTab('alerts')}
      />
      
      <main style={{ padding: '1.5rem 2rem', maxWidth: '1600px', margin: '0 auto' }}>
        {/* Tabs */}
        <div style={{ 
          display: 'flex', 
          gap: '0.35rem', 
          marginBottom: '1.5rem',
          background: colors.bgCard,
          padding: '0.35rem',
          borderRadius: '12px',
          width: 'fit-content',
          boxShadow: colors.cardShadow,
        }}>
          {tabs.map(({ id, icon: Icon, label }) => (
            <button
              key={id}
              onClick={() => setActiveTab(id)}
              style={{
                padding: '0.6rem 1rem',
                background: activeTab === id ? colors.primary : 'transparent',
                border: 'none',
                borderRadius: '8px',
                color: activeTab === id ? 'white' : colors.textSecondary,
                fontSize: '0.8rem',
                fontWeight: 600,
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '0.45rem',
                transition: 'all 0.2s ease',
                boxShadow: activeTab === id ? colors.primaryGlow : 'none',
              }}
              onMouseEnter={(e) => {
                if (activeTab !== id) {
                  e.currentTarget.style.background = colors.bgElevated;
                  e.currentTarget.style.color = colors.textPrimary;
                }
              }}
              onMouseLeave={(e) => {
                if (activeTab !== id) {
                  e.currentTarget.style.background = 'transparent';
                  e.currentTarget.style.color = colors.textSecondary;
                }
              }}
            >
              <Icon size={15} />
              {label}
            </button>
          ))}
        </div>
        
        {activeTab === 'overview' && (
          <>
            <div style={{ 
              display: 'grid', 
              gridTemplateColumns: 'repeat(6, 1fr)', 
              gap: '1rem', 
              marginBottom: '1.5rem',
            }}>
              <StatCard title="Total Shipments" value={stats.total} icon={Package} color={colors.primary} trend={{ value: 12, isPositive: true }} />
              <StatCard title="Delivered" value={stats.delivered} icon={CheckCircle} color={colors.success} />
              <StatCard title="In Transit" value={stats.inTransit} icon={Truck} color={colors.info} />
              <StatCard title="Excursions" value={stats.withExcursions} icon={AlertTriangle} color={colors.warning} />
              <StatCard title="Critical" value={stats.critical} icon={AlertCircle} color={colors.danger} />
              <StatCard title="Compliance" value={`${stats.complianceRate}%`} icon={CheckCircle} color={colors.success} />
            </div>
            
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 380px', gap: '1.25rem' }}>
              <ShipmentsTable shipments={data.shipments} onSelectShipment={setSelectedShipment} />
              <AnalyticsPanel shipments={data.shipments} excursions={data.excursions} />
            </div>
          </>
        )}
        
        {activeTab === 'cost' && <CostImpactCalculator shipments={data.shipments} excursions={data.excursions} />}
        {activeTab === 'predictive' && <PredictiveAnalytics shipments={data.shipments} excursions={data.excursions} />}
        {activeTab === 'routes' && <RouteRiskHeatmap shipments={data.shipments} excursions={data.excursions} />}
        {activeTab === 'timeline' && <ShipmentTimeline shipments={data.shipments} excursions={data.excursions} onSelectShipment={setSelectedShipment} />}
        {activeTab === 'alerts' && <AlertsPanel shipments={data.shipments} excursions={data.excursions} onSelectShipment={setSelectedShipment} />}
      </main>
      
      {selectedShipment && (
        <ShipmentModal shipment={selectedShipment} readings={data.readings} excursions={data.excursions} onClose={() => setSelectedShipment(null)} />
      )}
    </div>
  );
}

export default App;